import classNames from 'classnames';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';
import { useSort, type SortColumns } from '@/utils/use-sort';

import type { Trigger } from './api-types';
import { describeDelay, describeEvent } from './channels';
import MessagingNav from './messaging-nav';
import { ChannelList, SegmentList, Toggle, TriggerIcon } from './parts';

// Who it reaches, what it sends and the switch have no order worth sorting by.
const COLUMNS: SortColumns<Trigger> = {
  name: { value: (t) => t.name },
  wait: { value: (t) => t.delay_hours },
  sent: { value: (t) => t.sent_30d, desc: true },
};

/** Automated messages and what starts them (OUT-3). Which triggers exist is still open (Q14). */
const TriggersPage = () => {
  const navigate = useNavigate();
  const { data, error, reload } = useApi<Trigger[]>('/triggers');
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const open = (id: string) => navigate(`/messaging/triggers/${id}`);
  const { rows, sort, toggle } = useSort(data ?? [], COLUMNS);

  const setActive = async (t: Trigger, active: boolean) => {
    setBusy(t.id);
    setFailed(false);
    try {
      await http.post(`/triggers/${t.id}/active`, { active });
      reload();
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Triggers"
        description="Messages that go out on their own when something happens, once per casino or lead."
        actions={
          <Link to="/messaging/triggers/new" className={btn.primary}>
            New trigger
          </Link>
        }
      >
        <MessagingNav />
      </PageHeader>
      <p className="mb-4 text-sm text-fg-muted">
        The list of triggers is still to be defined (Q14, Matt). For now only “A casino goes live” and “A lead is added” send anything; the others are
        stored and marked “Not evaluated yet”. Switching a trigger on never sends for what happened before.
      </p>
      {failed && <p className="mb-4 text-sm text-danger">That did not work. Try again.</p>}

      {error && !data ? (
        <StateMessage title="Triggers could not be loaded" tone="error">
          {error}
        </StateMessage>
      ) : data && data.length === 0 ? (
        <StateMessage title="No triggers yet">
          <Link to="/messaging/triggers/new" className={btn.link}>
            Make one
          </Link>
        </StateMessage>
      ) : (
        <div className={table.wrap}>
          <div className="overflow-x-auto">
            <table className={classNames(table.table, 'md:min-w-[900px]')}>
              <thead className={table.head}>
                <tr>
                  <SortTh label="Trigger" column="name" sort={sort} onSort={toggle} />
                  <th scope="col" className={classNames(table.th, 'hidden md:table-cell')}>Who</th>
                  <SortTh label="Wait" column="wait" sort={sort} onSort={toggle} className="hidden md:table-cell" />
                  <th scope="col" className={classNames(table.th, 'hidden md:table-cell')}>Sends</th>
                  <SortTh label="Last 30 days" column="sent" sort={sort} onSort={toggle} right className="hidden md:table-cell" />
                  <th scope="col" className={table.th}>Active</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr
                    key={t.id}
                    tabIndex={0}
                    onClick={() => open(t.id)}
                    onKeyDown={(e) => {
                      // Only the row itself: Enter on the switch inside must not open the trigger.
                      if (e.key === 'Enter' && e.target === e.currentTarget) open(t.id);
                    }}
                    className={classNames(table.rowLink, 'focus:outline-none focus-visible:bg-surface-hover')}
                  >
                    <td className={table.td}>
                      <div className="flex items-start gap-3 md:items-center">
                        <span className={classNames('shrink-0', !t.evaluated && notYet)}>
                          <TriggerIcon event={t.event} />
                        </span>
                        <div className="min-w-0">
                          <p className={classNames('flex flex-wrap items-center gap-2 break-words text-fg-strong', !t.evaluated && notYet)}>
                            {t.name}
                            {t.demo && <DemoPill title="From the local demo seed">Demo</DemoPill>}
                          </p>
                          <p className={classNames('text-xs text-fg-muted', !t.evaluated && notYet)}>When: {describeEvent(t)}</p>
                          {/* Not in this version: faded, with the info mark beside the badge that says so. */}
                          {!t.evaluated && (
                            <span className="inline-flex items-center gap-1.5">
                              <span className={notYet}>
                                <Badge>Not evaluated yet</Badge>
                              </span>
                              <NotYetInfo what="trigger" />
                            </span>
                          )}
                          {/* On a phone the other columns fold in here. */}
                          <div className={classNames('mt-1.5 space-y-1 text-xs text-fg-muted md:hidden', !t.evaluated && notYet)}>
                            <SegmentList ids={t.segment_ids} empty="Everyone it happens to" />
                            <p>
                              {describeDelay(t.delay_hours)} · {t.template_name} · {t.sent_30d} in 30 days
                            </p>
                            <ChannelList channels={t.channels} iconsOnly />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={classNames(table.td, 'hidden md:table-cell', !t.evaluated && notYet)}>
                      <SegmentList ids={t.segment_ids} empty="Everyone it happens to" />
                    </td>
                    <td className={classNames(table.td, 'hidden whitespace-nowrap text-fg-muted md:table-cell', !t.evaluated && notYet)}>{describeDelay(t.delay_hours)}</td>
                    <td className={classNames(table.td, 'hidden md:table-cell', !t.evaluated && notYet)}>
                      <p className="text-fg">{t.template_name}</p>
                      <ChannelList channels={t.channels} iconsOnly />
                    </td>
                    <td className={classNames(table.td, 'hidden text-right tabular-nums md:table-cell', !t.evaluated && notYet)}>{t.sent_30d}</td>
                    <td className={classNames(table.td, 'align-top md:align-middle', !t.evaluated && notYet)}>
                      <span className="inline-flex flex-col items-center gap-1 text-xs text-fg-muted md:flex-row md:gap-2">
                        <Toggle label={`${t.name} active`} checked={t.active} disabled={busy === t.id} onChange={(v) => setActive(t, v)} />
                        {t.active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriggersPage;
